import { modalController, ModalOptions } from '@ionic/core';
import {
  BaseObject,
  CategorySlug,
  EnvString,
  FirestoreDataType,
  FirestoreObject,
  PlayerObject,
} from '@sworkit/api';
import { swApi } from '../../../global/app';
import { envKeyMap } from '../../../global/firestore-data';

export const requiredPlayerObjectFields: (keyof PlayerObject)[] = [
  'activityKey',
  'activityMFP',
  'activityWeight',
  'appleActivityHK',
  'avoidanceAreas',
  'bodyParts',
  'careAreas',
  'category',
  'equipmentTypes',
  'googleActivity',
  'recommendedAge',
];
export const requiredBaseObjectFields: (keyof BaseObject)[] = [
  'name',
  'classification',
  'releaseTimestamp',
  'slug',
];

export const getCategoryExtraFields = (
  category: CategorySlug,
): Partial<PlayerObject> => {
  //todo: I believe we're only using activityKey, activityMFP, and activityWeight in app. Verify and simplify.
  switch (category) {
    case 'strength':
      return {
        activityWeight: 6,
        googleActivity: 'STRENGTH_TRAINING',
        activityMFP: '134026252709869',
        appleActivityHK: 'HKWorkoutActivityTypeFunctionalStrengthTraining',
        activityKey: 'strength_training',
        goals: ['GOAL_BUILD'],
      };

    case 'cardio':
      return {
        activityWeight: 7,
        googleActivity: 'CIRCUIT_TRAINING',
        activityMFP: '134026252709869',
        appleActivityHK: 'HKWorkoutActivityTypeMixedMetabolicCardioTraining',
        activityKey: 'interval_training',
        goals: ['GOAL_ENDURANCE'],
      };
    case 'yoga':
      return {
        activityWeight: 4,
        googleActivity: 'YOGA',
        activityMFP: '133751232154941',
        appleActivityHK: 'HKWorkoutActivityTypeYoga',
        activityKey: 'yoga',
        goals: ['GOAL_FLEXIBILITY'],
      };
    case 'stretching':
      return {
        activityWeight: 3,
        googleActivity: 'CALISTHENICS',
        activityMFP: '133478623407981',
        appleActivityHK: 'HKWorkoutActivityTypePreparationAndRecovery',
        activityKey: 'preparation_and_recovery',
        goals: ['GOAL_FLEXIBILITY'],
      };
    case 'pilates':
      return {
        activityWeight: 6,
        googleActivity: 'PILATES',
        activityMFP: '133201476341181',
        appleActivityHK: 'HKWorkoutActivityTypeDanceInspiredTraining',
        activityKey: 'pilates',
        goals: ['GOAL_FLEXIBILITY'],
      };
    case 'tai-chi':
      return {
        activityWeight: 3,
        googleActivity: 'TAICHI',
        activityMFP: '133201476341181',
        appleActivityHK: 'HKWorkoutActivityTypeDanceInspiredTraining',
        activityKey: 'martial_arts',
        goals: ['GOAL_HEALTH'],
      };
    case 'pt':
      return {
        activityWeight: 3,
        googleActivity: 'CALISTHENICS',
        activityMFP: '133476496895469',
        appleActivityHK: 'HKWorkoutActivityTypeFlexibility',
        activityKey: 'flexibility',
        goals: ['GOAL_FLEXIBILITY'],
      };
    case 'barre':
      return {
        activityWeight: 5,
        googleActivity: 'CIRCUIT_TRAINING',
        activityMFP: '134028400160749',
        appleActivityHK: 'HKWorkoutActivityTypeBarre',
        activityKey: 'barre',
        goals: ['GOAL_FLEXIBILITY'],
      };
    case 'mobility':
      return {
        activityWeight: 3,
        googleActivity: 'CALISTHENICS',
        activityMFP: '133478623407981',
        appleActivityHK: 'HKWorkoutActivityTypeFunctionalStrengthTraining',
        activityKey: 'functional_strength',
        goals: ['GOAL_HEALTH', 'GOAL_INJURY'],
      };
    case 'dance':
      return {
        activityWeight: 3,
        googleActivity: 'CALISTHENICS',
        activityMFP: '134028400160749',
        appleActivityHK: 'HKWorkoutActivityTypeDanceInspiredTraining',
        activityKey: 'dancing',
        goals: ['GOAL_HEALTH'],
      };
    case 'boxing':
      return {
        activityWeight: 5,
        googleActivity: 'BOXING',
        activityMFP: '134028412776429',
        appleActivityHK: 'HKWorkoutActivityTypeBoxing',
        activityKey: 'boxing',
        goals: ['GOAL_BUILD'],
      };
    case 'pelvic':
      return {
        activityWeight: 3,
        googleActivity: 'CALISTHENICS',
        activityMFP: '133478623407981',
        appleActivityHK: 'HKWorkoutActivityTypeDance',
        activityKey: 'preparation_and_recovery',
        goals: ['GOAL_HEALTH'],
      };

    default:
      return {};
  }
};

export const routerKeyByDataType: { [K in FirestoreDataType | 'aiAudio']: any } = {
  assessmentExercises: {
    url: 'assessment-admin/exercises',
    key: 'assessment-exercise',
  },
  assessments: { url: 'assessment-admin/assessments', key: 'assessment' },
  exercises: { key: 'exercise' },
  workouts: { key: 'workout' },
  collections: { key: 'collection' },
  studioWorkouts: { key: 'studio-workout' },
  meditations: { key: 'meditation' },
  learningLibrary: { key: 'learning-library' },
  collectionCategories: { key: 'collection-category' },
  uiTranslations: { key: 'ui-translation' },
  aiAudio: {key: 'ai-audio' }
};

export async function openTranslationsModal() {
  const { dataType, docId, slug } = this;
  const modalOptions: ModalOptions = {
    backdropDismiss: false,
    component: 'ion-nav',
    cssClass: 'sw-large-modal',
    componentProps: {
      root: 'firestore-data-translation-edit',
      rootParams: {
        dataType,
        docId,
        slug,
      },
    },
  };

  try {
    const modal = await modalController.create(modalOptions);
    await modal.present();
  } catch (e) {
    console.error(e);
  }
}
export const slugsMustBeUniqueWithThese: FirestoreDataType[] = [
  'assessments',
  'workouts',
  'studioWorkouts',
  'collections',
  'meditations',
  'learningLibrary',
];

export function validateDeployingData(
  env: EnvString,
  dataType: FirestoreDataType,
  data: FirestoreObject[],
) {
  if (swApi?.search('cardio')) {
    if (dataType === 'uiTranslations' || dataType === 'assessmentExercises') {
      return;
    }
    let message = '';
    // showGlobalLoading({message: `Assessing all the deploying ${dataType}, please wait`})
    const filteredSlugsToDeploy = data
      .filter((d) => d?.[envKeyMap[env]])
      .map((d) => d.slug);
    const duplicates =
      [...new Set(filteredSlugsToDeploy)].length < filteredSlugsToDeploy.length;
    if (duplicates) {
      const dupes = getDuplicates(filteredSlugsToDeploy);
      message += `Found duplicate slugs: ${dupes} \n`;
    }
    const slugsAlreadyExistInOtherData = filteredSlugsToDeploy
      .map((slug) => swApi.search(slug))
      .filter((s) => s && s.dataType != dataType);

    console.log({ slugsAlreadyExistInOtherData });

    if (slugsAlreadyExistInOtherData.length) {
      message += `Change these slugs, they need to be unique: ${slugsAlreadyExistInOtherData.map(
        (d) => `"${d?.slug}" found in ${d?.dataType}\n`,
      )}  \n`;
    }
    return message;
  } else throw Error('no swapi');
}

function getDuplicates(arr) {
  const uniqueSet = new Set();
  const duplicates = [];

  for (const element of arr) {
    if (uniqueSet.has(element)) {
      duplicates.push(element);
    } else {
      uniqueSet.add(element);
    }
  }

  return duplicates;
}
